import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, CardBody, Table,
  Spinner, Modal, ModalHeader, ModalBody
} from 'reactstrap';
import {
  TbTrendingUp,
  TbUsers,
  TbClock,
  TbCheckbox,
  TbAlertCircle,
  TbChartPie,
  TbArrowUp,
  TbArrowDown,
  TbChartBar,
  TbCube,
  TbTarget,
  TbCalendar,
} from 'react-icons/tb';
import { motion } from 'framer-motion';
import 'chart.js/auto';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import './ModernDashboard.css';
import { getM3Gratis } from '../../../helpers/backend';

// Paleta de colores pastel en HEX/RGB
const pastel = {
  blue: '#9ec5fe',
  green: '#b9e6a1',
  orange: '#ffd8a8',
  purple: '#d8b4fe',
  pink: '#f5c2e7',
  teal: '#a3e3da',
  yellow: '#fff3b0',
  red: '#fecaca',
  lightBg: '#f3f4f6',
  darkText: '#334155',
  lightText: '#475569',
  white: '#ffffff'
};

const hexToRgba = (hex, alpha = 1) => {
  const h = hex.replace('#', '');
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getPctColorHex = (pct) => {
  if (pct >= 90) return pastel.red; // alto
  if (pct >= 70) return pastel.orange; // medio
  return pastel.green; // bajo
};

const ModernDashboard = ({ data, loading }) => {
  const [animatedStats, setAnimatedStats] = useState({
    totalOT: 0,
    otFinalizadas: 0,
    otEnProceso: 0,
    otPendientes: 0,
    porcentajeFinalizados: 0
  });

  const [m3DetailsOpen, setM3DetailsOpen] = useState(false);
  const [loadingM3Details, setLoadingM3Details] = useState(false);
  const [m3Details, setM3Details] = useState(null);
  const [m3Error, setM3Error] = useState(null);

  // Normaliza el nombre del estado para comparación segura
  const normalizeEstado = (estado) => (estado || '').replace(/\s+/g, '_').toUpperCase();

  // Obtiene el conteo por estado desde casos_por_estado
  const countByEstado = (estadoBuscado) => {
    const key = normalizeEstado(estadoBuscado);
    const item = data?.casos_por_estado?.find((i) => normalizeEstado(i.estado) === key);
    if (!item) return 0;
    return typeof item.cantidad === 'number' ? item.cantidad : (typeof item.total === 'number' ? item.total : 0);
  };

  // Animación mejorada de números
  useEffect(() => {
    if (data?.resumen_general && !loading) {
      const duration = 1500;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        const pend = countByEstado('PENDIENTE');
        const enProc = countByEstado('EN_PROCESO');
        const pendientesTarget = pend || (data.resumen_general.casos_pendientes || 0);
        const enProcesoTarget = enProc || (data.resumen_general.casos_en_proceso || 0);

        setAnimatedStats({
          totalOT: Math.floor((data.resumen_general.total_casos || 0) * easeOutQuart),
          otFinalizadas: Math.floor((data.resumen_general.casos_finalizados || 0) * easeOutQuart),
          otEnProceso: Math.floor(enProcesoTarget * easeOutQuart),
          otPendientes: Math.floor(pendientesTarget * easeOutQuart),
          porcentajeFinalizados: Math.floor((data.resumen_general.porcentaje_finalizados || 0) * easeOutQuart)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          const pendF = countByEstado('PENDIENTE');
          const enProcF = countByEstado('EN_PROCESO');
          setAnimatedStats({
            totalOT: data.resumen_general.total_casos || 0,
            otFinalizadas: data.resumen_general.casos_finalizados || 0,
            otEnProceso: enProcF || (data.resumen_general.casos_en_proceso || 0),
            otPendientes: pendF || (data.resumen_general.casos_pendientes || 0),
            porcentajeFinalizados: data.resumen_general.porcentaje_finalizados || 0
          });
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [data, loading]);

  // Configuración de gráficos con colores pastel
  const estadosChartData = {
    labels: data?.casos_por_estado?.map(item => {
      const estadoLabels = {
        'SIN_ASIGNAR': 'Sin Asignar',
        'FINALIZADO': 'Finalizado',
        'PENDIENTE': 'Pendiente',
        'EN_PROCESO': 'En Proceso'
      };
      const key = normalizeEstado(item.estado);
      return estadoLabels[key] || item.estado;
    }) || [],
    datasets: [{
      data: data?.casos_por_estado?.map(item => (typeof item.cantidad === 'number' ? item.cantidad : (typeof item.total === 'number' ? item.total : 0))) || [],
      backgroundColor: [
        '#A7C7E7', // Azul pastel
        '#C1E1C1', // Verde pastel
        '#FFD8A8', // Naranja pastel
        '#D8BFD8', // Lila pastel
        '#B5EAD7'  // Verde menta pastel
      ],
      borderColor: [
        '#8BB4D8',
        '#A8D5A8',
        '#FFC085',
        '#C9AEC9',
        '#95D4BC'
      ],
      borderWidth: 2,
      hoverOffset: 15,
    }]
  };

  const tiposChartData = {
    labels: data?.casos_por_tipo?.map(item => item.tipo_caso) || [],
    datasets: [{
      label: 'Cantidad de OT',
      data: data?.casos_por_tipo?.map(item => item.cantidad) || [],
      backgroundColor: '#A7C7E7',
      borderColor: '#8BB4D8',
      borderWidth: 1,
      borderRadius: 6,
    }]
  };

  const tiposOrdenChartData = {
    labels: data?.casos_por_tipo_orden?.map(item => item.tipo_orden) || [],
    datasets: [{
      label: 'Tipos de OT',
      data: data?.casos_por_tipo_orden?.map(item => item.cantidad) || [],
      backgroundColor: '#C1E1C1',
      borderColor: '#A8D5A8',
      borderWidth: 1,
      borderRadius: 6
    }]
  };

  const tendenciasChartData = {
    labels: data?.tendencias_semanales?.map(item => item.semana) || [],
    datasets: [{
      label: 'OT por Semana',
      data: data?.tendencias_semanales?.map(item => item.casos) || [],
      borderColor: '#A7C7E7',
      backgroundColor: 'rgba(167, 199, 231, 0.1)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#A7C7E7',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
          font: {
            size: 11,
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        titleFont: {
          size: 12
        },
        bodyFont: {
          size: 11
        },
        padding: 10,
        cornerRadius: 6,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    },
    elements: {
      line: {
        tension: 0.4
      }
    }
  };

  const m3DataRaw = data?.m3_gratis_mes ?? data?.contador_m3_gratis_mes;
  const m3Data = (typeof m3DataRaw === 'object' && m3DataRaw !== null)
    ? m3DataRaw
    : {
      solicitudes_actuales: Number(data?.contador_m3_gratis_mes) || 0,
      limite_solicitudes_mensual: Number(data?.limite_m3_gratis_mes) || 0,
      solicitudes_disponibles: undefined,
      casos_1m3: Number(data?.contador_m3_gratis_mes?.casos_1m3) || 0,
      casos_2m3: Number(data?.contador_m3_gratis_mes?.casos_2m3) || 0,
      m3_total_mes: Number(data?.m3_total_mes ?? 0) || 0
    };

  const m3GratisUsados = Number(m3Data?.solicitudes_actuales ?? 0);
  const m3GratisLimite = Number(m3Data?.limite_solicitudes_mensual ?? 0);
  const m3Disponible = Number(m3Data?.solicitudes_disponibles ?? Math.max(m3GratisLimite - m3GratisUsados, 0));
  const m3GratisPct = m3GratisLimite > 0 ? Math.min(100, Math.round((m3GratisUsados / m3GratisLimite) * 100)) : 0;
  const m3Casos1 = Number(m3Data?.casos_1m3 ?? 0);
  const m3Casos2 = Number(m3Data?.casos_2m3 ?? 0);
  const m3TotalMes = Number(m3Data?.m3_total_mes ?? data?.m3_total_mes ?? 0);

  const openM3Details = async () => {
    setM3Error(null);
    setM3DetailsOpen(true);
    setLoadingM3Details(true);
    try {
      const response = await getM3Gratis();
      setM3Details(response?.data || {});
    } catch (error) {
      console.error('Error al cargar M3 gratis:', error);
      setM3Error('No fue posible cargar los detalles de M3 gratis.');
    } finally {
      setLoadingM3Details(false);
    }
  };

  const closeM3Details = () => {
    setM3DetailsOpen(false);
  };

  const detallesActual = Number(m3Details?.solicitudes_actuales ?? m3Details?.contador_m3_gratis_mes ?? m3GratisUsados);
  const detallesLimite = Number(m3Details?.limite_solicitudes_mensual ?? m3Details?.limite_m3_gratis_mes ?? m3GratisLimite);
  const detallesDisponible = Number(m3Details?.solicitudes_disponibles ?? Math.max(detallesLimite - detallesActual, 0));
  const detallesAvance = detallesLimite > 0 ? Math.min(Math.round((detallesActual / detallesLimite) * 100), 100) : 0;
  const casosDetalleCombinados = [
    ...((m3Details?.casos_1m3_detalle ?? []).map(c => ({ ...c, unidades_gratis: Number(c?.unidades_gratis ?? 1) }))),
    ...((m3Details?.casos_2m3_detalle ?? []).map(c => ({ ...c, unidades_gratis: Number(c?.unidades_gratis ?? 2) }))),
    ...((m3Details?.casos ?? []))
  ];

  const statCards = [
    {
      title: 'Total de OT',
      value: animatedStats.totalOT,
      icon: TbChartPie,
      bgColorHex: hexToRgba(pastel.blue, 0.15),
      iconColorHex: pastel.blue,
      badgeBgHex: pastel.blue
    },
    {
      title: 'OT Finalizadas',
      value: animatedStats.otFinalizadas,
      icon: TbCheckbox,
      bgColorHex: hexToRgba(pastel.green, 0.15),
      iconColorHex: pastel.green,
      badgeBgHex: pastel.green,
      percentage: animatedStats.porcentajeFinalizados
    },
    {
      title: 'OT Pendientes',
      value: animatedStats.otPendientes,
      icon: TbAlertCircle,
      bgColorHex: hexToRgba(pastel.orange, 0.15),
      iconColorHex: pastel.orange,
      badgeBgHex: pastel.orange
    },
    {
      title: 'OT en Proceso',
      value: animatedStats.otEnProceso,
      icon: TbClock,
      bgColorHex: hexToRgba(pastel.purple, 0.15),
      iconColorHex: pastel.purple,
      badgeBgHex: pastel.purple
    }
  ];



  if (loading) {
    return (
      <div className="modern-dashboard-loading d-flex flex-column align-items-center justify-content-center">
        <div className="loading-spinner">
          <TbChartPie size={48} />
        </div>
        <p className="mt-3 text-muted">Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="modern-dashboard">
      <Container fluid className="py-3">

        {/* Tarjetas de estadísticas principales */}
        <Row className="mb-4">
          {statCards.map((stat, index) => (
            <Col lg={3} md={6} className="mb-3" key={stat.title}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1
                }}
              >
                <Card className={`stat-card h-100 border-2 shadow-lg`} style={{ backgroundColor: stat.bgColorHex }}>
                  <CardBody className="d-flex align-items-center">
                    <div className="stat-icon me-3" style={{ backgroundColor: hexToRgba(stat.iconColorHex, 0.2), color: stat.iconColorHex }}>
                      <stat.icon size={24} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-baseline">
                        <h3 className="stat-number mb-0 me-2">{stat.value.toLocaleString()}</h3>
                        {stat.percentage && (
                          <span className="badge fs-6" style={{ backgroundColor: stat.badgeBgHex, color: pastel.darkText }}>
                            {stat.percentage}%
                          </span>
                        )}
                      </div>
                      <p className="stat-label text-muted mb-0">{stat.title}</p>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* Solicitudes con M3 Gratis del Mes - Ajustado a solicitudes */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="border-2 shadow-lg">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center">
                    <div className="p-2 rounded me-3" style={{ backgroundColor: hexToRgba(pastel.green, 0.15) }}>
                      <TbCube size={24} style={{ color: pastel.green }} />
                    </div>
                    <div>
                      <h4 className="mb-1">Solicitudes con M3 Gratis (Mes)</h4>
                      <p className="text-muted mb-0">Progreso de solicitudes que usan beneficio de M3 gratis</p>
                    </div>
                  </div>
                  <button type="button" onClick={openM3Details} disabled={loadingM3Details} className="btn" style={{ backgroundColor: pastel.blue, borderColor: pastel.blue, color: pastel.darkText }}>
                    {loadingM3Details ? <Spinner size="sm" className="me-1" /> : <TbTarget className="me-1" style={{ color: pastel.darkText }} />}
                    Ver Detalles
                  </button>
                </div>

                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold">Progreso de Solicitudes Mensuales</span>
                        <span className="badge fs-6" style={{ backgroundColor: getPctColorHex(m3GratisPct), color: pastel.darkText }}>
                          {m3GratisPct}% solicitudes usadas
                        </span>
                      </div>
                      <div className="mb-2" style={{ backgroundColor: '#e5e7eb', height: '12px', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ width: `${m3GratisPct}%`, backgroundColor: getPctColorHex(m3GratisPct), height: '100%' }} />
                      </div>
                      <div className="d-flex justify-content-between text-sm text-muted">
                        <span>{m3GratisUsados} solicitudes usadas</span>
                        <span>{m3Disponible} disponibles</span>
                        <span>{m3GratisLimite} límite</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light rounded p-3">
                      <h5 className="text-center mb-3">Distribución por Tamaño</h5>
                      <Row className="text-center">
                        <Col xs={6} className="mb-2">
                          <div className="border-end">
                            <div className="h4 mb-1" style={{ color: pastel.blue }}>{m3Casos1}</div>
                            <small className="text-muted">Solicitudes 1 m³</small>
                          </div>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <div>
                            <div className="h4 mb-1" style={{ color: pastel.orange }}>{m3Casos2}</div>
                            <small className="text-muted">Solicitudes 2 m³</small>
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-3 text-center">
                        <small className="text-muted">Total M3 consumidos: <span className="fw-semibold" style={{ color: pastel.purple }}>{m3TotalMes}</span> m³</small>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-3 g-2">
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.blue, 0.15) }}>
                      <TbCube size={20} className="mb-1" style={{ color: pastel.blue }} />
                      <div className="h5 mb-1">{m3GratisUsados}</div>
                      <small className="text-muted">Solicitudes Usadas</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.pink, 0.15) }}>
                      <TbTarget size={20} className="mb-1" style={{ color: pastel.teal }} />
                      <div className="h5 mb-1">{m3GratisLimite}</div>
                      <small className="text-muted">Límite Solicitudes</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.green, 0.15) }}>
                      <TbCheckbox size={20} className="mb-1" style={{ color: pastel.green }} />
                      <div className="h5 mb-1">{m3Disponible}</div>
                      <small className="text-muted">Solicitudes Disponibles</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.orange, 0.15) }}>
                      <TbCalendar size={20} className="mb-1" style={{ color: pastel.orange }} />
                      <div className="h5 mb-1">{m3GratisPct}%</div>
                      <small className="text-muted">% Solicitudes Usadas</small>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal M3 Details */}
        <Modal isOpen={m3DetailsOpen} toggle={closeM3Details} size="xl">
          <ModalHeader toggle={closeM3Details} className="bg-light">
            <TbCube className="me-2" />
            Detalles de Solicitudes con M3 Gratis (Mes)
          </ModalHeader>
          <ModalBody>
            {m3Error && <div className="alert alert-danger">{m3Error}</div>}
            {loadingM3Details ? (
              <div className="d-flex justify-content-center my-4">
                <Spinner />
              </div>
            ) : (
              <>
                <Row className="mb-4">
                  <Col md={3} className="text-center">
                    <div className="border rounded p-3">
                      <div className="fs-4 fw-bold" style={{ color: pastel.blue }}>{detallesActual}</div>
                      <div className="text-muted">Solicitudes Usadas</div>
                    </div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="border rounded p-3">
                      <div className="fs-4 fw-bold" style={{ color: pastel.teal }}>{detallesLimite}</div>
                      <div className="text-muted">Límite Solicitudes</div>
                    </div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="border rounded p-3">
                      <div className="fs-4 fw-bold" style={{ color: pastel.green }}>{detallesDisponible}</div>
                      <div className="text-muted">Solicitudes Disponibles</div>
                    </div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="border rounded p-3">
                      <div className="fs-4 fw-bold" style={{ color: pastel.orange }}>{detallesAvance}%</div>
                      <div className="text-muted">Avance Solicitudes</div>
                    </div>
                  </Col>
                </Row>

                <h5 className="mb-3">Solicitudes con Beneficio de M3 Gratis</h5>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>OT</th>
                        <th>Cliente</th>
                        <th>Agente</th>
                        <th className="text-end">M3 Gratis por Caso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {casosDetalleCombinados.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4">
                            No hay solicitudes con M3 gratis este mes.
                          </td>
                        </tr>
                      ) : (
                        casosDetalleCombinados.map((caso, idx) => (
                          <tr key={caso?.id ?? idx}>
                            <td className="fw-semibold">{caso?.name ?? '-'}</td>
                            <td>{caso?.cliente ?? caso?.cliente_nombre ?? '-'}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                                  {(caso?.contacto ?? caso?.contacto_nombre ?? '?').charAt(0).toUpperCase()}
                                </div>
                                {caso?.contacto ?? caso?.contacto_nombre ?? '-'}
                              </div>
                            </td>
                            <td className="text-end fw-bold">
                              {caso?.unidades_gratis ?? '0'} m³
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>

        {/* Gráficos principales */}
        <Row className="mb-4">
          {[
            { data: estadosChartData, title: 'OT por Estado', icon: TbChartPie, component: Doughnut, lg: 6 },
            { data: tiposChartData, title: 'OT por Fuente', icon: TbTrendingUp, component: Bar, lg: 6 },
            { data: tiposOrdenChartData, title: 'Casos por Tipo de OT', icon: TbTrendingUp, component: Bar, lg: 6 },
            { data: tendenciasChartData, title: 'Tendencias Semanales', icon: TbChartBar, component: Line, lg: 6 }
          ].map((chart, index) => (
            <Col lg={chart.lg} className="mb-3" key={chart.title}>
              <Card className="border-2 shadow-lg h-100">
                <CardBody>
                  <h5 className="card-title d-flex align-items-center">
                    <chart.icon className="me-2" />
                    {chart.title}
                  </h5>
                  <div className="chart-container">
                    <chart.component data={chart.data} options={chartOptions} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>


      </Container>
    </div>
  );
};

export default ModernDashboard;